// Shared handling of the Tyro claimable-item reference list: one fetch per session, the SYSTEM/PRACTICE
// overlap resolved, and the type-ahead search both the ECLIPSE claim form and the patient claimable item
// picker run over it.
app.service('TyroClaimableItems', [
	'$q',
	'TyroAPI',
	'Session',
	function ($q, TyroAPI, Session) {

		// Held as the promise rather than the list so concurrent callers share one request.
		var loaded = null;
		// The practice the cached list was loaded for: practice items (and their fees) differ per practice,
		// and switching practice does not reload the page.
		var loadedPracticeId = null;

		var service = {

			// Cached for the life of the page: reference data that only changes when the practice edits its
			// own items. Pass true to force a refetch after such an edit.
			load: function (forceRefresh) {
				var practiceId = Session.practice && Session.practice.id;
				if (loaded && !forceRefresh && loadedPracticeId === practiceId) {
					return loaded;
				}

				var request = TyroAPI.getClaimableItems().then(function (response) {
					return prepare(response.data || []);
				}).catch(function (error) {
					// A failed load must not stick, or one bad response breaks the drop-down for the session.
					if (loaded === request) {
						loaded = null;
					}
					return $q.reject(error);
				});

				loaded = request;
				loadedPracticeId = practiceId;
				return loaded;
			},

			clear: function () {
				loaded = null;
			},

			// Prefix match on the item code or on any word of its name or description, so "109" finds 10915
			// and "glau" finds Optometry Glaucoma Examination. Every word typed must match, which lets
			// "glau exam" narrow further.
			search: function (items, search) {
				var terms = (search || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
				if (terms.length === 0) {
					return items || [];
				}
				return (items || []).filter(function (item) {
					return terms.every(function (term) {
						return item.$searchWords.some(function (word) { return word.indexOf(term) === 0; });
					});
				});
			},

			label: function (item) {
				if (!item) {
					return '';
				}
				return item.name ? item.item_code + ' - ' + item.name : item.item_code;
			},

			// Exposed so a caller holding an item that is not in the reference list - a snapshot recorded
			// before the practice changed its items - can still offer it in the same drop-down.
			tokenise: tokenise,

			isPracticeItem: isPracticeItem
		};

		// Drops unavailable items and resolves the SYSTEM/PRACTICE overlap: a practice item supersedes the
		// SYSTEM default whose code it duplicates, as both read identically in the drop-down and only the
		// practice's own copy carries the fee it charges.
		function prepare(items) {
			var available = items.filter(function (item) { return item.available !== false; });

			var practiceCodes = available
				.filter(isPracticeItem)
				.map(function (item) { return item.item_code; });

			return available
				.filter(function (item) {
					return isPracticeItem(item) || practiceCodes.indexOf(item.item_code) === -1;
				})
				.map(tokenise)
				.sort(function (a, b) { return (a.item_code || '').localeCompare(b.item_code || ''); });
		}

		// Legacy rows written before the type split carry no item_type and are CatTrax defaults, which the
		// backend also treats as SYSTEM.
		function isPracticeItem(item) {
			return item.item_type === 'PRACTICE';
		}

		// search_term / other_search_terms already carry the code, name, description and funder, but are
		// optional on the record, so the code and name are included directly to guarantee searchability.
		function tokenise(item) {
			item.$searchWords = [item.item_code, item.name, item.search_term, item.other_search_terms]
				.filter(Boolean)
				.join(' ')
				.toLowerCase()
				.split(/[^a-z0-9]+/)
				.filter(Boolean);
			return item;
		}

		return service;
	},
]);
